// Globals
import React, { ChangeEvent, ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash.debounce';
import dayjs, { Dayjs } from 'dayjs';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { changeQuery, getBannersList, selectQuery } from '../../feature';

// Components
import { StyledFilterRow } from './BannersFiltersContainerStyles';
import { DatePicker, Input, Select } from '@/ui';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Helpers
import { BannersFiltersFields, getActiveOptions } from '../../helpers';
import { GetBannersListDTO } from '../../dtos';

type IBannersFiltersContainer = {
  children?: ReactNode;
  className?: string;
};

const BannersFiltersContainer = ({ children, className }: IBannersFiltersContainer) => {
  const { t } = useTranslation(['banners']);
  const dispatch = useAppDispatch();
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();
  // Mirror the table column layout: the leading drag-handle column and the
  // trailing actions column only exist when the admin can manage banners.
  const canManageBanners = canManage(Resource.BANNER);

  const handleDebounceSearch = useMemo(
    () =>
      debounce(async (searchParams) => {
        const dto = new GetBannersListDTO(searchParams);
        dispatch(getBannersList(dto));
      }, 500),
    [dispatch],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const newQuery = { ...query, [name]: value || undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleDateChange = (name: string) => (date: Dayjs) => {
    const newQuery = { ...query, [name]: date ? dayjs(date).format('YYYY-MM-DD') : undefined };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  const handleBooleanFilter = (name: string) => (value: string) => {
    const newQuery = {
      ...query,
      [name]: value,
    };
    dispatch(changeQuery(newQuery));
    handleDebounceSearch(newQuery);
  };

  return (
    <thead className={className}>
      {children}
      <StyledFilterRow>
        {canManageBanners && <th />}
        <th>
          <Input
            placeholder={t('banners_list_title')}
            name={BannersFiltersFields.TITLE}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <Input
            placeholder={t('banners_list_text')}
            name={BannersFiltersFields.DESCRIPTION}
            onChange={handleSearch}
            size="large"
            allowClear
          />
        </th>
        <th>
          <DatePicker onChange={handleDateChange(BannersFiltersFields.START_DATE)} size="middle" />
        </th>
        <th>
          <DatePicker onChange={handleDateChange(BannersFiltersFields.END_DATE)} size="middle" />
        </th>
        <th>
          <Select
            options={getActiveOptions(t)}
            placeholder={t('banners_list_status')}
            onChange={handleBooleanFilter(BannersFiltersFields.ACTIVE)}
            size="large"
            allowClear
          />
        </th>
        {canManageBanners && <th />}
      </StyledFilterRow>
    </thead>
  );
};

export default BannersFiltersContainer;
